import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  CollectionReference,
  DocumentReference,
  Query,
  QueryConstraint,
  addDoc,
  collection,
  collectionSnapshots,
  deleteDoc,
  doc,
  docData,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

export type FirestoreQueryFn = (ref: CompatCollectionRef) => CompatCollectionRef;

class CompatCollectionRef {
  readonly constraints: QueryConstraint[] = [];

  where(field: string, op: string, value: unknown): this {
    this.constraints.push(where(field, op as any, value));
    return this;
  }
}

function buildFirestoreCollection<T>(
  colRef: CollectionReference<T>,
  queryFn?: FirestoreQueryFn,
): AppFirestoreCollection<T> {
  if (!queryFn) {
    return new AppFirestoreCollection(colRef, colRef);
  }
  const builder = new CompatCollectionRef();
  queryFn(builder);
  const queryRef = query(colRef, ...builder.constraints);
  return new AppFirestoreCollection(colRef, queryRef);
}

export class AppFirestoreCollection<T = unknown> {
  constructor(
    private readonly colRef: CollectionReference<T>,
    private readonly queryRef: Query<T> | CollectionReference<T>,
  ) {}

  get ref(): { get: () => ReturnType<typeof getDocs> } {
    return {
      get: () => getDocs(this.queryRef),
    };
  }

  snapshotChanges(): Observable<Array<{ payload: { doc: { id: string; data: () => T } } }>> {
    return collectionSnapshots(this.queryRef).pipe(
      map((snaps) =>
        snaps.map((snap) => ({
          payload: {
            doc: {
              id: snap.id,
              data: () => snap.data() as T,
            },
          },
        })),
      ),
    );
  }

  valueChanges(): Observable<T[]> {
    return collectionSnapshots(this.queryRef).pipe(
      map((snaps) => snaps.map((snap) => snap.data() as T)),
    );
  }

  get(): Observable<ReturnType<typeof getDocs> extends Promise<infer R> ? R : never> {
    return from(getDocs(this.queryRef));
  }

  add(data: T): Promise<DocumentReference<T>> {
    return addDoc(this.colRef, data);
  }

  doc(id: string): AppFirestoreDocument<T> {
    return new AppFirestoreDocument(doc(this.colRef, id));
  }
}

export class AppFirestoreDocument<T = unknown> {
  constructor(private readonly docRef: DocumentReference<T>) {}

  valueChanges(): Observable<T | undefined> {
    return docData(this.docRef) as Observable<T | undefined>;
  }

  set(data: T, options?: { merge?: boolean }): Promise<void> {
    return setDoc(this.docRef, data, options);
  }

  update(data: Partial<T>): Promise<void> {
    return updateDoc(this.docRef, data as any);
  }

  delete(): Promise<void> {
    return deleteDoc(this.docRef);
  }

  collection<R = unknown>(
    path: string,
    queryFn?: FirestoreQueryFn,
  ): AppFirestoreCollection<R> {
    const colRef = collection(this.docRef, path) as CollectionReference<R>;
    return buildFirestoreCollection(colRef, queryFn);
  }
}

@Injectable({
  providedIn: 'root',
})
export class AppFirestoreService {
  private readonly firestore = inject(Firestore);

  collection<T = unknown>(path: string, queryFn?: FirestoreQueryFn): AppFirestoreCollection<T> {
    const colRef = collection(this.firestore, path) as CollectionReference<T>;
    return buildFirestoreCollection(colRef, queryFn);
  }

  doc<T = unknown>(path: string): AppFirestoreDocument<T> {
    return new AppFirestoreDocument(doc(this.firestore, path) as DocumentReference<T>);
  }

  createId(): string {
    return doc(collection(this.firestore, '_ids')).id;
  }
}
